import React from 'react';
import { isEmpty, isNil } from 'lodash';

import Chip from '~/components/core/Atomic/Chip/Chip';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { ChevronDownIcon, ChevronUpIcon } from '~/components/icons';
import Checkbox from '~/components/ui/atoms/Checkbox';
import Collapse from '~/components/ui/atoms/Collapse';
import PROMOTION_PRUNED_REFERENCES_NOTE from '~/server_shared/generated-types/PROMOTION_PRUNED_REFERENCES_NOTE';
import { prettifyKey } from '~/Utils';
import cn from '~/Utils/cn';

import BlockedSectionGroup from './BlockedSectionGroup';
import ConflictCard from './ConflictCard';
import ConflictCardReorder from './ConflictCardReorder';
import { CHANGE_TYPE_LABEL } from './constants';
import DiffValue from './DiffValue';
import OrderList from './OrderList';
import type { ScopeDimension, ScopeFilterSets } from './promotionScope';
import { GLOBAL_SCOPE_VALUE, prunedValuesFor, SCOPE_DIMENSIONS, scopeValuesFor } from './promotionScope';
import { formatValue, groupChangesByType, isMaterialConflict, objectLabel, reorderResult } from './promotionsUtils';
import ReorderMoversSummary from './ReorderMoversSummary';
import type {
  ChangeGroup,
  ConflictChoice,
  PromotionBlockedSection,
  PromotionChange,
  PromotionConflict,
  PromotionConflictResolution,
  PromotionTypeCounts,
} from './types';
import { useScopeValueLabel } from './useScopeValueLabel';

// How many scope values a change's scope row shows inline before collapsing the rest behind a "+N" that
// reveals them on hover — keeps a change attributed to many members from blowing out the row.
const MAX_INLINE_SCOPE_VALUES = 4;

// Empty highlight sets, so `ChangeScopeRows` can be rendered without an active filter (nothing highlighted).
const NO_SCOPE_HIGHLIGHT: ScopeFilterSets = { sub_organizations: new Set(), lobs: new Set() };

// Default for `lockedChangeIds` — nothing locked (the common no-new-member promotion).
const NO_LOCKED_CHANGES: Set<string> = new Set();

// Default for `blockedSections` — no section's export is blocked.
const NO_BLOCKED_SECTIONS: PromotionBlockedSection[] = [];

// Tooltip on a locked new-member CREATE checkbox, pointing back to the interim step that owns the decision.
const NEW_MEMBER_LOCK_TOOLTIP =
  'This is a new member being created by this promotion. Promote it or leave it out in the “New members” ' +
  'step — it can’t be toggled here, so a reference to it can’t be kept while the member itself is dropped.';

// Default for `requiredBy` — no row is required by a selected item.
const NO_REQUIRED_BY: Map<string, string[]> = new Map();

// Tooltip on the checkbox of a CREATE that a selected Metabase item references.
const REQUIRED_DEPENDENCY_LOCK_TOOLTIP =
  'A selected Metabase item references this one and it does not exist on the target yet, so it is promoted ' +
  'with it. Deselect every item that requires it to change this.';

/** A row checkbox that can't be toggled here, with the reason as its tooltip. */
const LockedRowCheckbox: React.FC<{ title: string; checked: boolean }> = ({ title, checked }) => (
  <Tooltip title={title} arrow>
    {/* A disabled checkbox sets pointer-events:none, so re-enable them on the wrapper span or the tooltip
        never fires on hover. */}
    <span className="pointer-events-auto inline-flex">
      <Checkbox size="xs" disabled checked={checked} />
    </span>
  </Tooltip>
);

/** One scope value pill; highlighted when it matches the active facet filter (why this line is included). */
const ScopeValuePill: React.FC<{ label: string; highlighted: boolean }> = ({ label, highlighted }) => (
  <span
    className={cn(
      'py-1 rounded px-4',
      highlighted ? 'bg-blue-100 font-medium text-blue-900' : 'bg-slate-100 text-slate-800'
    )}
  >
    {label}
  </span>
);

/** The comma-separated (as pills) member list for one dimension of a change: an unscoped dimension shows a
 * single explicit "Global" pill; a long list truncates to the first {@link MAX_INLINE_SCOPE_VALUES}
 * with a "+N" that reveals the remainder on hover. Values matching the active filter are highlighted;
 * matching is keyed on the raw values while `labelFor` maps them to display names at render. */
const ScopeValueList: React.FC<{ values: string[]; highlight: Set<string>; labelFor: (value: string) => string }> = ({
  values,
  highlight,
  labelFor,
}) => {
  if (values.length === 0) {
    return <ScopeValuePill label={labelFor(GLOBAL_SCOPE_VALUE)} highlighted={highlight.has(GLOBAL_SCOPE_VALUE)} />;
  }
  const shown = values.slice(0, MAX_INLINE_SCOPE_VALUES);
  const overflow = values.slice(MAX_INLINE_SCOPE_VALUES);
  return (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {shown.map((value) => (
        <ScopeValuePill key={value} label={labelFor(value)} highlighted={highlight.has(value)} />
      ))}
      {overflow.length > 0 && (
        <Tooltip title={overflow.map(labelFor).join(', ')} arrow>
          <span className="py-1 rounded bg-slate-100 px-4 text-slate-800 underline decoration-dotted">
            +{overflow.length}
          </span>
        </Tooltip>
      )}
    </span>
  );
};

/** Maps a raw scope value to its display name — `useScopeValueLabel`'s return, threaded down from
 * `ChangeListByType` so the per-row surfaces don't each re-derive it (see the hoisted call there). */
type ScopeValueLabel = (dimension: ScopeDimension, value: string) => string;

/** The two labeled scope rows shown under a change's title when "Show scopes" is on — Sub-orgs and LOBs, each
 * as its own row so the two dimensions never interleave into mixed chips. */
const ChangeScopeRows: React.FC<{
  change: PromotionChange;
  highlight: ScopeFilterSets;
  scopeValueLabel: ScopeValueLabel;
}> = ({ change, highlight, scopeValueLabel }) => (
  <div className="flex flex-col gap-y-2 pl-8 text-xs">
    {SCOPE_DIMENSIONS.map((dimension) => (
      <div key={dimension.key} className="flex flex-wrap items-baseline gap-x-4">
        <span className="font-medium text-slate-800">{dimension.label}:</span>
        <ScopeValueList
          values={scopeValuesFor(change, dimension.key)}
          highlight={highlight[dimension.key]}
          labelFor={(value) => scopeValueLabel(dimension.key, value)}
        />
      </div>
    ))}
  </div>
);

/** A per-change badge naming the scope references pruned during cross-org projection — members absent from
 * the target that were dropped from the promoted config — shown beside the change title like the collision
 * badge. Renders nothing when nothing was pruned. */
const PrunedReferencesBadge: React.FC<{ change: PromotionChange; scopeValueLabel: ScopeValueLabel }> = ({
  change,
  scopeValueLabel,
}) => {
  const labels = SCOPE_DIMENSIONS.flatMap((dimension) =>
    prunedValuesFor(change, dimension.key).map((value) => scopeValueLabel(dimension.key, value))
  );
  if (labels.length === 0) {
    return null;
  }
  return <span className="text-orange-700">⚠ references pruned ({labels.join(', ')})</span>;
};

/** What else the change does on the target, as the server wrote it on the change: the configurations a
 * Default field row reaches, a configuration it resets, or a pruned reference where no badge shows it. */
const ChangeWarnings: React.FC<{ warnings?: string[]; prunedReferencesBadgeShown?: boolean }> = ({
  warnings,
  prunedReferencesBadgeShown = false,
}) => {
  // The server words each prune it also records in `change.pruned` with this note, which the badge names.
  const shown = prunedReferencesBadgeShown
    ? warnings?.filter((warning) => !warning.includes(`${PROMOTION_PRUNED_REFERENCES_NOTE}:`))
    : warnings;
  return isEmpty(shown) ? null : (
    <ul className="m-0 list-none p-0 text-orange-700">
      {shown?.map((warning) => <li key={warning}>⚠ {warning}</li>)}
    </ul>
  );
};

function countsSummaryText(counts?: PromotionTypeCounts): string {
  if (!counts) {
    return '';
  }
  const parts: string[] = [];
  if (counts.create_count) {
    parts.push(`${counts.create_count} created`);
  }
  if (counts.update_count) {
    parts.push(`${counts.update_count} updated`);
  }
  if (counts.delete_count) {
    parts.push(`${counts.delete_count} deleted`);
  }
  return parts.join(' · ');
}

/** Per-type CREATE/UPDATE/DELETE counts derived from an actual change list. Used in the selectable
 * (create) flow so the group header reflects the selected subset rather than the whole-preview
 * `summary`, keeping it in sync with the checked rows and the backend's selection-scoped counts.
 *
 * REORDER counts as an update, matching `PromotionRequestTypeCounts.list_from_summary` on the server.
 * Counting it nowhere would render a reorder-only group with no counts at all — the same "reports as
 * nothing" bug, just on the other side of the wire. */
function countsFromChanges(configuration_type: string, changes: PromotionChange[]): PromotionTypeCounts {
  const counts: PromotionTypeCounts = { configuration_type, create_count: 0, update_count: 0, delete_count: 0 };
  for (const change of changes) {
    if (change.change_type === 'CREATE') {
      counts.create_count = (counts.create_count ?? 0) + 1;
    } else if (change.change_type === 'UPDATE' || change.change_type === 'REORDER') {
      counts.update_count = (counts.update_count ?? 0) + 1;
    } else if (change.change_type === 'DELETE') {
      counts.delete_count = (counts.delete_count ?? 0) + 1;
    }
  }
  return counts;
}

/** Per-field old→new (UPDATE) or new-only (CREATE/DELETE) rendering of a change's `object_changes`,
 * mirroring the Audit Log's Object Changes column. Used for every non-conflict change row. */
const ChangeDeltaList: React.FC<{ change: PromotionChange }> = ({ change }) => {
  const deltas = change.object_changes ?? [];
  if (deltas.length === 0) {
    return null;
  }
  // A whole-object DELETE arrives as a single object_change whose new_value is null (see the backend's
  // _build_deletion_change) — a "<field>: ∅" line that's pure noise, since the row's chip + breadcrumb
  // already convey the deletion. Suppress only that empty line: a single-delta DELETE that carries a real
  // new_value (a field-level removal) keeps its delta, and multi-field deletes keep theirs.
  const onlyDelta = deltas.length === 1 ? deltas[0] : undefined;
  if (
    change.change_type === 'DELETE' &&
    onlyDelta &&
    (onlyDelta.new_value === null || onlyDelta.new_value === undefined)
  ) {
    return null;
  }
  const isUpdate = change.change_type === 'UPDATE';
  // A created object's single delta is the whole serialized body keyed by its sync-key — the object is
  // already named by the row's breadcrumb, so that key label is noise. Field-level deltas keep their descs.
  const hideDesc = change.change_type === 'CREATE' && deltas.length === 1;
  return (
    <div className="flex flex-col gap-y-4 pl-8 text-sm">
      {deltas.map((delta, idx) => (
        <div key={`${delta.desc ?? ''}-${idx}`} className="flex flex-wrap items-center gap-x-4">
          {delta.desc && !hideDesc && <span>{delta.desc}:</span>}
          {isUpdate ? (
            <>
              <DiffValue value={delta.old_value} tone="old" />
              <span>→</span>
              <DiffValue value={delta.new_value} tone="new" />
            </>
          ) : (
            <DiffValue value={delta.new_value} tone="new" />
          )}
        </div>
      ))}
    </div>
  );
};

/** Named-mover "what moved" summary plus the full Before / After order behind a collapse toggle, for the
 * read-only `ReorderDeltaList`. */
const ReorderOrderSummary: React.FC<{
  before: string[];
  after: string[];
  keyLabels: Record<string, string>;
  // Keys the promoted order actually names. Anything in `after` that is absent from this list survives
  // by staying put rather than by being promoted, which is worth showing: it is the difference between
  // "this stays" and "this was left out".
  promotedKeys?: string[];
}> = ({ before, after, keyLabels, promotedKeys }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (before.length === 0 && after.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-4 text-sm">
      {/* The promoted order is `promotedKeys`, or `after` itself on a change stored without a resolved order. */}
      <ReorderMoversSummary before={before} after={after} keyLabels={keyLabels} promotedOrder={promotedKeys ?? after} />
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} full order`}
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-fit items-center rounded border-0 bg-transparent p-0 text-sm text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
      >
        {expanded ? (
          <ChevronUpIcon size={20} iconColor="currentColor" />
        ) : (
          <ChevronDownIcon size={20} iconColor="currentColor" />
        )}
      </button>
      <Collapse in={expanded} unmountOnExit>
        <div className="grid grid-cols-2 gap-x-16">
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-800">Before</span>
            <OrderList keys={before} keyLabels={keyLabels} />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-800">After</span>
            <OrderList keys={after} keyLabels={keyLabels} before={before} promotedKeys={promotedKeys} />
          </div>
        </div>
      </Collapse>
    </div>
  );
};

/** Read-only rendering of a REORDER change: the "what moved" summary + collapsed full order (see
 * `ReorderOrderSummary`), labeled "Before"/"After". Mirrors `ChangeDeltaList`'s role for CREATE/UPDATE/
 * DELETE changes. */
const ReorderDeltaList: React.FC<{ change: PromotionChange }> = ({ change }) => {
  const before = Array.isArray(change.before_value) ? (change.before_value as string[]) : [];
  // Show the RESULT, not the promoted keys — see `reorderResult`.
  const { order: after, promotedKeys } = reorderResult(change);
  if (before.length === 0 && after.length === 0) {
    return null;
  }
  return (
    <div className="pl-8">
      <ReorderOrderSummary
        before={before}
        after={after}
        keyLabels={change.key_labels ?? {}}
        promotedKeys={promotedKeys}
      />
    </div>
  );
};

interface ChangeListByTypeProps {
  changes: PromotionChange[];
  summary: PromotionTypeCounts[];
  // When provided, renders per-change + per-group selection checkboxes (create flow). Omit for the
  // read-only detail dialogs.
  selectedChangeIds?: Set<string>;
  // New-member CREATE rows whose selection is pinned to the interim "New members" step: their per-change
  // checkbox is disabled (checked iff the row is in `selectedChangeIds`) and they're excluded from the
  // group / select-all toggle math. Empty (the default) → nothing locked.
  lockedChangeIds?: Set<string>;
  // CREATE rows a selected Metabase item references → the labels of the selected items requiring each. Such a
  // row's checkbox is disabled and it reads "Required by …". Unlike `lockedChangeIds`, it stays in the group /
  // select-all toggle math, so a bulk uncheck reaches it. Empty (the default) → nothing required.
  requiredBy?: Map<string, string[]>;
  onToggleChanges?: (changeIds: string[], checked: boolean) => void;
  // Create-flow collapse state: the set of configuration_types whose change-rows are currently collapsed
  // (empty by default → every section expanded). Owned by the parent so its Expand-all/Collapse-all toolbar
  // can toggle them together; only the selectable (create) flow renders the collapse affordance.
  collapsedGroups?: Set<string>;
  onToggleGroupCollapsed?: (configurationType: string) => void;
  // Create-flow scope display: when true, each change row shows its Sub-orgs / LOBs scope beneath the title.
  // `scopeHighlight` names the active facet-filter selections so the values that matched (why the line is
  // shown) are highlighted. Only the selectable (create) flow passes these.
  showScopes?: boolean;
  scopeHighlight?: ScopeFilterSets;
  // Create-flow prune surfacing: when true, a change carrying pruned scope references shows a "references
  // pruned" badge beside its title (mirrors the collision badge). Only the selectable (create) flow passes
  // it; the read-only detail dialogs leave it false.
  showPrunedReferences?: boolean;
  // Conflict-resolution mode (approve-time): maps a conflicting change_id to its conflict. When provided,
  // the list switches to the conflicts-first layout: every conflict is hoisted into a top section as a
  // decision card offering "Keep current" / "Use incoming"; non-conflicting changes apply automatically
  // and are shown grouped below. Neither option is pre-selected and the approver must pick one for every
  // conflict — `conflictDecisions` holds the choices made so far and `onResolveConflict` records each pick.
  conflicts?: Record<string, PromotionConflict>;
  conflictDecisions?: Record<string, ConflictChoice>;
  onResolveConflict?: (changeId: string, choice: ConflictChoice) => void;
  // Sections left out of the change list because one side's export of them is blocked. Rendered after the
  // groups in every mode as disabled groups (no checkbox, outside every count) with their reasons.
  blockedSections?: PromotionBlockedSection[];
}

/** Read-only rendering of one configuration-type group: a bordered card with the type header + per-type
 * counts and one compact old→new row per change. Shared by the read-only detail preview and the conflict
 * view's "applies automatically" section, so the two are identical by construction. */
const ChangeGroupCard: React.FC<{ group: ChangeGroup }> = ({ group }) => (
  <div className="rounded-md border border-solid border-slate-300">
    <div className="flex items-center justify-between bg-slate-100 px-12 py-8">
      <span className="font-bold">{prettifyKey(group.configuration_type)}</span>
      <span className="text-slate-800">
        {countsSummaryText(countsFromChanges(group.configuration_type, group.changes))}
      </span>
    </div>
    <div className="flex flex-col divide-y divide-slate-200">
      {group.changes.map((change) => (
        <div key={change.change_id} className="flex flex-col gap-y-8 px-12 py-8">
          <div className="flex items-center gap-x-12">
            <Chip size="small" label={CHANGE_TYPE_LABEL[change.change_type] ?? change.change_type} color="default" />
            <span className="font-medium">{objectLabel(change)}</span>
          </div>
          <ChangeWarnings warnings={change.warnings} />
          {change.change_type === 'REORDER' ? (
            <ReorderDeltaList change={change} />
          ) : (
            <ChangeDeltaList change={change} />
          )}
        </div>
      ))}
    </div>
  </div>
);

/** A flat change list grouped by configuration type, rendered read-only (no selection, no conflict
 * controls). The shared building block for the detail preview and the conflict view's auto-applies list. */
const ReadOnlyChangeGroups: React.FC<{ changes: PromotionChange[]; summary: PromotionTypeCounts[] }> = ({
  changes,
  summary,
}) => (
  <div className="flex flex-col gap-16">
    {groupChangesByType(changes, summary).map((group) => (
      <ChangeGroupCard key={group.configuration_type} group={group} />
    ))}
  </div>
);

/** Conflict-resolution layout (approve-time). Only *material* conflicts — where an override would clobber
 * a value someone changed on the target — need a decision; they're hoisted into a top "Needs a decision"
 * section as collapsible cards, each offering Keep current / Use incoming. Every other change
 * (non-conflicting, plus conflicts whose drift the promotion wouldn't actually overwrite) applies
 * automatically and is shown below in the same read-only grouped format as the preview. A REORDER conflict
 * is always material (see `isMaterialConflict`) and renders via `ReorderConflictCard`
 * instead of `ConflictCard`. */
const ConflictResolutionList: React.FC<{
  changes: PromotionChange[];
  summary: PromotionTypeCounts[];
  conflicts: Record<string, PromotionConflict>;
  decisions: Record<string, ConflictChoice>;
  onResolveConflict?: (changeId: string, choice: ConflictChoice) => void;
}> = ({ changes, summary, conflicts, decisions, onResolveConflict }) => {
  const [materialConflicts, autoChanges] = React.useMemo(() => {
    const material: PromotionChange[] = [];
    const auto: PromotionChange[] = [];
    for (const change of changes) {
      const conflict = conflicts[change.change_id];
      if (conflict === undefined) {
        auto.push(change);
        continue;
      }
      // Same predicate `useConflictResolution` uses for the resolve payload (see `isMaterialConflict`).
      if (isMaterialConflict(change, conflict)) {
        material.push(change);
      } else {
        // A conflict whose drift the promotion wouldn't overwrite → applies automatically.
        auto.push(change);
      }
    }
    return [material, auto];
  }, [changes, conflicts]);
  const decidedCount = materialConflicts.filter((c) => decisions[c.change_id] !== undefined).length;

  return (
    <div className="flex flex-col gap-16">
      {materialConflicts.length > 0 && (
        <div className="flex flex-col gap-12">
          <div className="flex items-center justify-between">
            <span className="font-bold">Needs a decision</span>
            <span className="text-slate-800">
              {decidedCount} of {materialConflicts.length} decided
            </span>
          </div>
          <div className="flex flex-col gap-12">
            {materialConflicts.map((change) =>
              change.change_type === 'REORDER' ? (
                <ConflictCardReorder
                  key={change.change_id}
                  change={change}
                  conflict={conflicts[change.change_id]}
                  choice={decisions[change.change_id]}
                  onResolveConflict={onResolveConflict}
                />
              ) : (
                <ConflictCard
                  key={change.change_id}
                  change={change}
                  conflict={conflicts[change.change_id]}
                  choice={decisions[change.change_id]}
                  onResolveConflict={onResolveConflict}
                />
              )
            )}
          </div>
        </div>
      )}

      {autoChanges.length > 0 && (
        <div className="flex flex-col gap-8">
          <span className="text-slate-800">The following changes have no conflicts and apply automatically:</span>
          <ReadOnlyChangeGroups changes={autoChanges} summary={summary} />
        </div>
      )}
    </div>
  );
};

/** The change groups of {@link ChangeListByType}, without its blocked sections. */
const ChangeGroupList: React.FC<Omit<ChangeListByTypeProps, 'blockedSections'>> = ({
  changes,
  summary,
  selectedChangeIds,
  lockedChangeIds = NO_LOCKED_CHANGES,
  requiredBy = NO_REQUIRED_BY,
  onToggleChanges,
  collapsedGroups,
  onToggleGroupCollapsed,
  showScopes = false,
  scopeHighlight = NO_SCOPE_HIGHLIGHT,
  showPrunedReferences = false,
  conflicts,
  conflictDecisions,
  onResolveConflict,
}) => {
  // Display names for the per-change scope rows and the pruned badge — resolved once for the whole list and
  // threaded down rather than per row: `useScopeValueLabel` rebuilds its LOB dictionary on every call (see
  // `useLobConfiguration`), and a create-flow preview can run to hundreds of rows.
  const scopeValueLabel = useScopeValueLabel();

  if (conflicts !== undefined) {
    return (
      <ConflictResolutionList
        changes={changes}
        summary={summary}
        conflicts={conflicts}
        decisions={conflictDecisions ?? {}}
        onResolveConflict={onResolveConflict}
      />
    );
  }

  const groups = groupChangesByType(changes, summary);
  const selectable = selectedChangeIds !== undefined;

  if (groups.length === 0) {
    return <div className="text-slate-800">No configuration changes.</div>;
  }

  // Read-only preview (no selection): the shared grouped renderer, identical to the conflict view's
  // auto-applies section.
  if (!selectable) {
    return <ReadOnlyChangeGroups changes={changes} summary={summary} />;
  }

  return (
    <div className="flex flex-col gap-16">
      {groups.map((group) => {
        const groupSelectableIds = selectable
          ? group.changes.filter((c) => !lockedChangeIds.has(c.change_id)).map((c) => c.change_id)
          : [];
        const selectedInGroup = group.changes.filter((c) => selectedChangeIds?.has(c.change_id));
        const groupSelectedCount = selectedInGroup.length;
        // The group checkbox governs only the toggleable (non-locked) rows; locked rows keep their pinned
        // state. With no locks this is identical to the previous all-rows math.
        const toggleableSelectedCount = groupSelectableIds.filter((id) => selectedChangeIds?.has(id)).length;
        const allInGroupSelected =
          groupSelectableIds.length > 0 && toggleableSelectedCount === groupSelectableIds.length;
        const someInGroupSelected = toggleableSelectedCount > 0 && !allInGroupSelected;
        const counts = selectable ? countsFromChanges(group.configuration_type, selectedInGroup) : group.counts;
        const headerRight = selectable && groupSelectedCount === 0 ? 'None selected' : countsSummaryText(counts);
        const groupLabel = prettifyKey(group.configuration_type);
        const collapsed = collapsedGroups?.has(group.configuration_type) ?? false;
        return (
          <div key={group.configuration_type} className="rounded-md border border-solid border-slate-300">
            <div className="flex items-center justify-between bg-slate-100 px-12 py-8">
              <span className="flex items-center gap-x-8 font-bold">
                {/* A group with no toggleable rows (e.g. only a locked new-member CREATE) shows no group
                    checkbox: toggling it is a no-op, and an unchecked/non-indeterminate header would otherwise
                    contradict the checked-and-disabled row beneath it. */}
                {selectable && groupSelectableIds.length > 0 && (
                  <Checkbox
                    size="xs"
                    checked={allInGroupSelected}
                    indeterminate={someInGroupSelected}
                    onChange={(_e, checked) => onToggleChanges?.(groupSelectableIds, checked)}
                  />
                )}
                {groupLabel}
              </span>
              <span className="flex items-center gap-x-8">
                <span className="text-slate-800">{headerRight}</span>
                {onToggleGroupCollapsed && (
                  <button
                    type="button"
                    aria-expanded={!collapsed}
                    aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${groupLabel}`}
                    onClick={() => onToggleGroupCollapsed(group.configuration_type)}
                    className="flex items-center rounded border-0 bg-transparent p-0 text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                  >
                    {collapsed ? (
                      <ChevronDownIcon size={20} iconColor="currentColor" />
                    ) : (
                      <ChevronUpIcon size={20} iconColor="currentColor" />
                    )}
                  </button>
                )}
              </span>
            </div>
            <Collapse in={!collapsed} unmountOnExit>
              <div className="flex flex-col divide-y divide-slate-200">
                {group.changes.map((change) => {
                  const flaggedCollision = Boolean(change.conflict);
                  const memberLocked = lockedChangeIds.has(change.change_id);
                  const requiredByLabels = requiredBy.get(change.change_id);
                  return (
                    <div key={change.change_id} className="flex flex-col gap-y-8 px-12 py-8">
                      <div className="flex items-center gap-x-12">
                        {selectable &&
                          (memberLocked || !isNil(requiredByLabels) ? (
                            <LockedRowCheckbox
                              title={memberLocked ? NEW_MEMBER_LOCK_TOOLTIP : REQUIRED_DEPENDENCY_LOCK_TOOLTIP}
                              checked={selectedChangeIds?.has(change.change_id) ?? false}
                            />
                          ) : (
                            <Checkbox
                              size="xs"
                              checked={selectedChangeIds?.has(change.change_id) ?? false}
                              onChange={(_e, checked) => onToggleChanges?.([change.change_id], checked)}
                            />
                          ))}
                        <Chip
                          size="small"
                          label={CHANGE_TYPE_LABEL[change.change_type] ?? change.change_type}
                          color={flaggedCollision ? 'secondary' : 'default'}
                        />
                        <span className="font-medium">{objectLabel(change)}</span>
                        {!isNil(requiredByLabels) && (
                          <span className="text-slate-700">Required by {requiredByLabels.join(', ')}</span>
                        )}
                        {flaggedCollision && <span className="text-orange-700">⚠ collision</span>}
                        {showPrunedReferences && (
                          <PrunedReferencesBadge change={change} scopeValueLabel={scopeValueLabel} />
                        )}
                      </div>
                      <ChangeWarnings warnings={change.warnings} prunedReferencesBadgeShown={showPrunedReferences} />
                      {showScopes && (
                        <ChangeScopeRows change={change} highlight={scopeHighlight} scopeValueLabel={scopeValueLabel} />
                      )}
                      {change.change_type === 'REORDER' ? (
                        <ReorderDeltaList change={change} />
                      ) : (
                        <ChangeDeltaList change={change} />
                      )}
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </div>
        );
      })}
    </div>
  );
};

/** Renders a promotion's flat change list grouped by configuration type, with per-type counts and a
 * per-change row showing the target object and its field-level diffs (old → new). Shared by the
 * create-preview (selectable) and the read-only outgoing/incoming detail dialogs. Conflict-resolution
 * mode (when `conflicts` is provided) is delegated to `ConflictResolutionList`. Blocked sections follow the
 * groups in every mode. */
const ChangeListByType: React.FC<ChangeListByTypeProps> = ({ blockedSections = NO_BLOCKED_SECTIONS, ...listProps }) => {
  if (blockedSections.length === 0) {
    return <ChangeGroupList {...listProps} />;
  }
  return (
    <div className="flex flex-col gap-16">
      <ChangeGroupList {...listProps} />
      {blockedSections.map((section) => (
        <BlockedSectionGroup key={`${section.side}:${section.configuration_type}`} section={section} />
      ))}
    </div>
  );
};

export default ChangeListByType;

/** Read-only summary of a recorded conflict-resolution decision, shown on a resolved request: which
 * conflicts the approver overrode (overwrote the target) vs skipped (kept the target's value), with the
 * value that was there at decision time. */
export const ConflictResolutionSummary: React.FC<{ resolution: PromotionConflictResolution }> = ({ resolution }) => {
  const { overridden, skipped } = resolution;
  if (overridden.length === 0 && skipped.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-y-8 rounded-md border border-solid border-slate-300 px-12 py-8">
      <span className="font-bold">Conflict resolution</span>
      {overridden.length > 0 && (
        <div className="flex flex-col gap-y-4 text-sm">
          <span className="text-slate-800">Overrode {overridden.length} conflict(s) — overwrote the target:</span>
          {overridden.map((c) => (
            <div key={c.change_id} className="flex flex-wrap items-center gap-x-4 pl-8">
              <span className="font-medium">{c.field_path.split('.').map(prettifyKey).join(' → ')}:</span>
              <span className="text-slate-800">was</span>
              <span className="rounded bg-red-100 px-4">{formatValue(c.actual_current)}</span>
            </div>
          ))}
        </div>
      )}
      {skipped.length > 0 && (
        <div className="flex flex-col gap-y-4 text-sm">
          <span className="text-slate-800">Skipped {skipped.length} conflict(s) — kept the current target value:</span>
          {skipped.map((c) => (
            <div key={c.change_id} className="flex flex-wrap items-center gap-x-4 pl-8">
              <span className="font-medium">{c.field_path.split('.').map(prettifyKey).join(' → ')}:</span>
              <span className="text-slate-800">kept</span>
              <span className="rounded bg-orange-100 px-4">{formatValue(c.actual_current)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
